import React, { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { Dialog, DialogTitle, DialogBody } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Text } from './text';
import { useUser } from './UserContext';
import { logBitacora } from '../utils/bitacora';
import { PENDIENTE_TIPO_LABELS, sePuedePedirAlFiado } from '../utils/pendientes';

const MESSAGE_MAX_LEN = 500;
const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

/**
 * Ask the fiado for one or more pendientes in a single email.
 *
 * Batching is deliberate: a fiado who owes three things gets one email listing
 * three, not three emails. All the pendientes must belong to the same cliente
 * — the recipient set is derived from it.
 *
 * Recipients load the same way NotifyFianzaDialog loads them (cliente's
 * correoContacto + every client account with access), all preselected and each
 * deselectable, plus free-typed extras. The callable re-derives the allowed set
 * server-side and intersects, so this list is a convenience, not the authority.
 */
export function PendienteEmailDialog({
  open, onClose, pendientes = [], clientId, clientName, onSent,
}) {
  const { user, userData } = useUser();
  const [recipients, setRecipients] = useState(null); // null while loading
  const [customEmail, setCustomEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Endosos and compromisos con la afianzadora never reach the fiado by
  // mail (see NO_SE_PIDE_AL_FIADO); the row action is hidden for them, so
  // this filter only matters for a caller that passes a mixed list.
  const enviables = pendientes.filter((p) => sePuedePedirAlFiado(p.tipo));

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    setRecipients(null);
    setCustomEmail('');
    setMessage('');
    setError(null);
    (async () => {
      const list = [];
      const seen = new Set();
      try {
        const clienteSnap = await getDoc(doc(db, 'clientes', clientId));
        const clienteData = clienteSnap.exists() ? clienteSnap.data() : {};
        if (clienteData.correoContacto && clienteData.correoContacto.includes('@')) {
          seen.add(clienteData.correoContacto.toLowerCase());
          list.push({
            email: clienteData.correoContacto,
            nombre: clienteData.nombreContacto || clienteData.nombreEmpresa || '',
            hasAccount: false,
            selected: true,
          });
        }
        if (userData?.org) {
          const accountsSnap = await getDocs(query(
            collection(db, 'usuarios'),
            where('org', '==', userData.org),
            where('rol', '==', 'client'),
            where('clientIds', 'array-contains', clientId),
          ));
          accountsSnap.forEach((accountDoc) => {
            const account = accountDoc.data();
            const correo = account.correo || '';
            if (correo.includes('@') && !seen.has(correo.toLowerCase())) {
              seen.add(correo.toLowerCase());
              list.push({
                email: correo,
                nombre: account.nombre || '',
                hasAccount: true,
                selected: true,
              });
            }
          });
        }
      } catch (e) {
        console.error('Failed to load pendiente recipients:', e);
      }
      if (!cancelled) setRecipients(list);
    })();
    return () => { cancelled = true; };
  }, [open, clientId, userData?.org]);

  const toggleRecipient = (email) => {
    setRecipients((prev) => prev && prev.map((r) =>
      r.email === email ? { ...r, selected: !r.selected } : r
    ));
  };

  const addCustomEmail = () => {
    const email = customEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    setRecipients((prev) => {
      const list = prev || [];
      const hit = list.find((r) => r.email.toLowerCase() === email.toLowerCase());
      if (hit) {
        return list.map((r) => (r === hit ? { ...r, selected: true } : r));
      }
      return [...list, { email, nombre: '', hasAccount: false, custom: true, selected: true }];
    });
    setCustomEmail('');
  };

  const handleSend = async () => {
    const selected = (recipients || []).filter((r) => r.selected);
    if (selected.length === 0 || enviables.length === 0) return;
    setSending(true);
    setError(null);
    try {
      const send = httpsCallable(getFunctions(), 'sendPendienteEmail');
      const res = await send({
        clienteId: clientId,
        pendienteIds: enviables.map((p) => p.id),
        recipients: selected.map((r) => r.email),
        message: message.trim(),
      });
      const sentTo = res.data?.sent || selected.map((r) => r.email);
      try {
        const titulos = enviables.map((p) => p.titulo).join('; ');
        await logBitacora({
          action: 'UPDATE',
          collection: 'clientes',
          documentId: clientId,
          user,
          userData,
          metadata: {
            clientId,
            clientName: clientName || 'Sin nombre',
            description:
              `Solicitó por correo a ${sentTo.join(', ')}: ${titulos}`,
            pendienteIds: enviables.map((p) => p.id),
          },
        });
      } catch (logError) {
        console.error('Failed to log pendiente email:', logError);
      }
      onSent?.(sentTo);
      onClose();
    } catch (err) {
      console.error('Error sending pendiente email:', err);
      setError(err?.message || 'No se pudo enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  const selectedCount = (recipients || []).filter((r) => r.selected).length;

  return (
    <Dialog size="md" open={open} onClose={sending ? () => {} : onClose}>
      <DialogTitle>
        <span className="text-azul-surelia">Solicitar por correo</span>
      </DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {clientName && (
            <Text className="text-sm text-zinc-600">{clientName}</Text>
          )}

          {enviables.length === 0 ? (
            <Text className="text-sm text-zinc-600">
              Esto se tramita con la afianzadora; no hay nada que pedirle al fiado por correo.
            </Text>
          ) : (
            <div className="rounded-lg border border-azul-surelia/15 divide-y divide-azul-surelia/10">
              {enviables.map((p) => (
                <div key={p.id} className="px-3 py-2">
                  <Text className="text-sm font-medium text-zinc-800">{p.titulo}</Text>
                  <Text className="text-xs text-zinc-500">
                    {PENDIENTE_TIPO_LABELS[p.tipo] || p.tipo}
                  </Text>
                </div>
              ))}
            </div>
          )}

          {recipients === null ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-azul-surelia"></div>
            </div>
          ) : (
            <>
              {recipients.length === 0 ? (
                <Text className="text-sm text-zinc-600">
                  El fiado no tiene correos registrados.
                </Text>
              ) : (
                <div className="border border-azul-surelia/15 rounded-lg divide-y divide-azul-surelia/10">
                  {recipients.map((r) => (
                    <label
                      key={r.email}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={r.selected}
                        onChange={() => toggleRecipient(r.email)}
                        className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                      />
                      <div className="min-w-0 flex-1">
                        <Text className="text-sm text-zinc-800 truncate">{r.email}</Text>
                        {(r.nombre || r.hasAccount) && (
                          <Text className="text-xs text-zinc-500 truncate">
                            {r.nombre}
                            {r.nombre && r.hasAccount ? ' — ' : ''}
                            {r.hasAccount ? 'Cuenta de portal' : ''}
                          </Text>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  type="email"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomEmail(); } }}
                  placeholder="Agregar otro correo"
                />
                <Button plain onClick={addCustomEmail}>Agregar</Button>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Mensaje (opcional)
                </label>
                <textarea
                  rows={3}
                  value={message}
                  maxLength={MESSAGE_MAX_LEN}
                  onChange={(e) => setMessage(e.target.value)}
                  className={field}
                />
              </div>
            </>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={onClose} disabled={sending}>Cancelar</Button>
            <Button
              color="surelia"
              onClick={handleSend}
              disabled={sending || selectedCount === 0 || enviables.length === 0}
            >
              {sending ? 'Enviando…' : 'Enviar'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default PendienteEmailDialog;
